/**
 * @fileoverview Enhanced API Client with Error Handling, Timeout, and Retry Logic
 * 
 * This module provides robust HTTP client functionality for the Astrantis React application.
 * It includes comprehensive error handling, automatic retries, timeout management, and
 * authentication error handling.
 * 
 * @version 2.0.0
 * @author Astrantis Development Team
 * @since 2025-01-27
 * 
 * ## Features
 * 
 * - **Typed Error Handling**: Custom error classes for different failure scenarios
 * - **Automatic Retries**: Exponential backoff for transient failures
 * - **Timeout Management**: Configurable request timeouts with AbortController
 * - **Authentication Handling**: Global auth error callback system
 * - **Backward Compatibility**: Maintains existing API while adding new features
 * 
 * ## Error Types
 * 
 * - `TimeoutError`: Request exceeded timeout limit
 * - `NetworkError`: Connection or network failures
 * - `AuthenticationError`: 401/403 responses (triggers global handler)
 * - `ServerError`: 5xx server errors
 * - `ClientError`: 4xx client errors (except auth)
 * 
 * ## Usage Examples
 * 
 * ### Basic Usage (Backward Compatible)
 * ```javascript
 * import { getJson, postJson } from './services/apiClient.js';
 * 
 * // Simple GET request
 * const data = await getJson('/api/users');
 * 
 * // Simple POST request
 * const result = await postJson('/api/users', { name: 'John' });
 * ```
 * 
 * ### Advanced Usage with Options
 * ```javascript
 * // Custom timeout and retries
 * const data = await getJson('/api/slow-endpoint', {
 *   timeout: 60000,  // 60 seconds
 *   retries: 5,      // 5 attempts
 *   headers: { 'X-Custom': 'value' }
 * });
 * 
 * // POST with custom options
 * const result = await postJson('/api/upload', payload, {
 *   timeout: 120000,  // 2 minutes
 *   retries: 1,       // Only 1 retry
 *   headers: { 'Content-Type': 'application/json' }
 * });
 * ```
 * 
 * ### Error Handling
 * ```javascript
 * import { 
 *   AuthenticationError, 
 *   ServerError, 
 *   ClientError,
 *   TimeoutError,
 *   NetworkError 
 * } from '../utils/errors.js';
 * 
 * try {
 *   const data = await getJson('/api/protected');
 * } catch (error) {
 *   if (error instanceof AuthenticationError) {
 *     // Handle auth errors (global handler also called)
 *     console.log('Authentication failed:', error.message);
 *   } else if (error instanceof ServerError) {
 *     // Handle server errors
 *     console.log('Server error:', error.status, error.message);
 *   } else if (error instanceof TimeoutError) {
 *     // Handle timeouts
 *     console.log('Request timed out after:', error.timeout);
 *   } else if (error instanceof NetworkError) {
 *     // Handle network issues
 *     console.log('Network error:', error.message);
 *   }
 * }
 * ```
 * 
 * ### Global Authentication Handler
 * ```javascript
 * import { setAuthErrorHandler } from './services/apiClient.js';
 * 
 * // Set up global auth error handler
 * setAuthErrorHandler((error) => {
 *   console.log('Auth error detected:', error.message);
 *   // Redirect to login, clear tokens, etc.
 *   window.location.href = '/login';
 * });
 * ```
 * 
 * ## Configuration
 * 
 * Default values can be modified by changing the constants:
 * - `DEFAULT_TIMEOUT`: 30000ms (30 seconds)
 * - `MAX_RETRIES`: 3 attempts
 * - `RETRY_DELAY`: 1000ms base delay (exponential backoff: 1s, 2s, 4s)
 * - `RETRY_STATUSES`: [408, 429, 500, 502, 503, 504]
 * 
 * ## Migration Guide
 * 
 * ### From v1.x to v2.x
 * 
 * **No breaking changes** - existing code continues to work:
 * ```javascript
 * // Old code (still works)
 * const data = await getJson('/api/users');
 * 
 * // New code (enhanced features)
 * const data = await getJson('/api/users', { timeout: 60000 });
 * ```
 * 
 * **Enhanced error handling**:
 * ```javascript
 * // Old error handling
 * try {
 *   const data = await getJson('/api/users');
 * } catch (error) {
 *   console.log('Error:', error.message);
 * }
 * 
 * // New error handling (recommended)
 * try {
 *   const data = await getJson('/api/users');
 * } catch (error) {
 *   if (error instanceof AuthenticationError) {
 *     // Specific handling
 *   } else if (error.isRetryable()) {
 *     // Could retry
 *   }
 * }
 * ```
 * 
 * @see {@link ../utils/errors.js} For error class definitions
 * @see {@link ../config/api.js} For API configuration
 */

// src/services/apiClient.js
import { 
  APIError, 
  TimeoutError, 
  NetworkError, 
  AuthenticationError,
  ServerError,
  ClientError 
} from '../utils/errors.js';

// Global authentication error handler
let authErrorHandler = null;

/**
 * Set global authentication error handler
 * @param {Function} handler - Function to call on auth errors
 */
export function setAuthErrorHandler(handler) {
  authErrorHandler = handler;
}

// ============================================================================
// CONFIGURATION CONSTANTS
// ============================================================================

/**
 * Default request timeout in milliseconds
 * @constant {number}
 * @default 30000
 */
const DEFAULT_TIMEOUT = 30000; // 30 seconds

/**
 * Maximum number of retry attempts for failed requests
 * @constant {number}
 * @default 3
 */
const MAX_RETRIES = 3;

/**
 * Base delay between retry attempts in milliseconds
 * Uses exponential backoff: delay * 2^attempt (1s, 2s, 4s)
 * @constant {number}
 * @default 1000
 */
const RETRY_DELAY = 1000; // 1 second base delay

// ============================================================================
// INTERNAL UTILITY FUNCTIONS
// ============================================================================

/**
 * /api 요청에 localStorage auth_token 을 Authorization: Bearer 로 붙인다.
 * 이미 Authorization 이 있으면 덮어쓰지 않는다.
 */
function attachBearerAuthHeader(url, headers) {
  const next = new Headers(headers || {});
  if (next.has('Authorization')) return next;

  const urlStr = typeof url === 'string' ? url : '';
  const isApi =
    urlStr.startsWith('/api') ||
    /\/api(\/|$|\?)/.test(urlStr);
  if (!isApi) return next;

  try {
    const token =
      typeof localStorage !== 'undefined'
        ? localStorage.getItem('auth_token')
        : null;
    if (token) {
      next.set('Authorization', `Bearer ${token}`);
    }
  } catch {
    /* ignore */
  }
  return next;
}

/**
 * Performs a fetch request with configurable timeout using AbortController
 * 
 * This function wraps the native fetch API with timeout functionality.
 * If the request exceeds the specified timeout, it will be aborted and
 * a TimeoutError will be thrown.
 * 
 * Supports external AbortController signal for request cancellation.
 * If an external signal is provided, aborting it will cancel the request.
 * The timeout mechanism still applies independently.
 * 
 * @param {string} url - The URL to fetch
 * @param {Object} options - Standard fetch options (method, headers, body, etc.)
 * @param {number} [timeout=DEFAULT_TIMEOUT] - Timeout in milliseconds
 * @returns {Promise<Response>} The fetch Response object
 * @throws {TimeoutError} When request exceeds timeout limit
 * @throws {NetworkError} When network request fails (connection issues)
 * 
 * @example
 * // Basic usage with default timeout
 * const response = await fetchWithTimeout('/api/data', { method: 'GET' });
 * 
 * @example
 * // Custom timeout
 * const response = await fetchWithTimeout('/api/slow', { 
 *   method: 'POST', 
 *   body: JSON.stringify(data) 
 * }, 60000); // 60 second timeout
 * 
 * @example
 * // With external AbortController signal
 * const controller = new AbortController();
 * const response = await fetchWithTimeout('/api/data', { 
 *   method: 'GET',
 *   signal: controller.signal 
 * });
 * 
 * @private
 * @since 2.0.0
 * @version 2.2.0 - Added external signal support
 */
async function fetchWithTimeout(url, options, timeout = DEFAULT_TIMEOUT) {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);
  
  // Extract external signal if provided
  const externalSignal = options?.signal;
  let externalAborted = false;
  
  // If external signal is provided, listen for abort events
  if (externalSignal) {
    // Check if external signal is already aborted
    if (externalSignal.aborted) {
      clearTimeout(timeoutId);
      // Throw AbortError directly (not NetworkError)
      throw new DOMException('Request was aborted', 'AbortError');
    }
    
    // Listen for external abort
    externalSignal.addEventListener('abort', () => {
      externalAborted = true;
      controller.abort();
    });
  }

  // 개발·프로덕션 공통: /api 요청에 localStorage Bearer 첨부
  // (HttpOnly 쿠키만으로는 갱신 직후·크로스 오리진에서 불완전할 수 있음)
  const headers = attachBearerAuthHeader(url, options?.headers);
  
  try {
    const response = await fetch(url, {
      ...options,
      headers,
      signal: controller.signal
    });
    clearTimeout(timeoutId);
    return response;
  } catch (error) {
    clearTimeout(timeoutId);
    
    if (error.name === 'AbortError') {
      // Distinguish between timeout and external abort
      if (externalAborted || (externalSignal && externalSignal.aborted)) {
        // External abort - rethrow as AbortError (DOMException) for normal cancellation handling
        throw new DOMException('Request was aborted', 'AbortError');
      } else {
        // Timeout
        throw new TimeoutError(`Request timeout after ${timeout}ms`, timeout);
      }
    }
    
    // Handle fetch API standard errors
    if (error instanceof TypeError) {
      // CORS errors, network failures, invalid URLs
      throw new NetworkError(`Network request failed: ${error.message}`, error);
    }
    
    if (error instanceof DOMException) {
      // AbortController related errors
      if (error.name === 'AbortError') {
        throw error; // Re-throw as-is
      }
      throw new NetworkError(`Request failed: ${error.message}`, error);
    }
    
    // Re-throw other errors as-is for proper handling in calling code
    throw error;
  }
}

/**
 * Performs a fetch request with automatic retry logic and exponential backoff
 * 
 * This function implements intelligent retry logic for handling transient failures.
 * It uses exponential backoff (1s, 2s, 4s delays) and only retries errors that
 * are marked as retryable by the error's isRetryable() method.
 * 
 * Retryable errors include:
 * - Network errors (connection failures)
 * - Timeout errors
 * - Server errors (5xx status codes)
 * 
 * Non-retryable errors include:
 * - Authentication errors (401/403)
 * - Client errors (4xx except auth)
 * - Malformed requests
 * 
 * @param {string} url - The URL to fetch
 * @param {Object} options - Standard fetch options (method, headers, body, etc.)
 * @param {number} [retries=MAX_RETRIES] - Maximum number of retry attempts
 * @returns {Promise<Response>} The fetch Response object
 * @throws {APIError} Any error from fetchWithTimeout or HTTP error responses
 * 
 * @example
 * // Basic usage with default retry settings
 * const response = await fetchWithRetry('/api/unreliable', { method: 'GET' });
 * 
 * @example
 * // Custom retry count
 * const response = await fetchWithRetry('/api/critical', { 
 *   method: 'POST', 
 *   body: JSON.stringify(data) 
 * }, 5); // 5 retry attempts
 * 
 * @private
 * @since 2.0.0
 */
async function fetchWithRetry(url, options, retries = MAX_RETRIES, timeout = DEFAULT_TIMEOUT) {
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      return await fetchWithTimeout(url, options, timeout);
    } catch (error) {
      const isLastAttempt = attempt === retries;
      
      // Don't retry if aborted by external signal
      // Check multiple conditions for robustness across different environments
      if (
        error.name === 'AbortError' || 
        error.code === 'ERR_CANCELED' ||
        (error instanceof DOMException && error.name === 'AbortError')
      ) {
        throw error;
      }
      
      const shouldRetry = error.isRetryable && error.isRetryable();
      
      if (isLastAttempt || !shouldRetry) {
        throw error;
      }
      
      // Exponential backoff: 1s, 2s, 4s
      const delay = RETRY_DELAY * Math.pow(2, attempt);
      
      // 재시도 진행 상황 콜백 호출
      if (options?.onRetryProgress) {
        options.onRetryProgress({
          attempt: attempt + 1,      // 현재 시도 횟수 (1-based)
          maxRetries: retries,        // 최대 재시도 횟수
          delay: delay,               // 다음 재시도까지 대기 시간 (ms)
          error: error,               // 발생한 에러
          isRetrying: true            // 재시도 중 여부
        });
      }
      
      console.log(`Retry attempt ${attempt + 1}/${retries} after ${delay}ms for ${url}`);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
}

/**
 * Handles errors from fetch operations with proper type classification
 * 
 * This function provides centralized error handling for all public API functions.
 * It ensures that AbortError (normal cancellation) is not wrapped, and properly
 * classifies other error types into appropriate APIError subclasses.
 * 
 * @param {Error} error - The error to handle
 * @throws {APIError|DOMException} Re-throws APIError or AbortError as-is
 * @private
 * @since 2.3.0
 */
function handleFetchError(error) {
  // Re-throw APIError instances (including subclasses) as-is
  if (error instanceof APIError) {
    throw error;
  }
  
  // AbortError is a normal cancellation, don't wrap it
  if (error.name === 'AbortError' || error.code === 'ERR_CANCELED') {
    throw error;
  }
  
  // DOMException's AbortError should also be re-thrown as-is
  if (error instanceof DOMException && error.name === 'AbortError') {
    throw error;
  }
  
  // TypeError indicates CORS errors, network failures, invalid URLs
  if (error instanceof TypeError) {
    throw new NetworkError(`Network request failed: ${error.message}`, error);
  }
  
  // Other DOMException types (browser-level errors)
  if (error instanceof DOMException) {
    throw new NetworkError(`Request failed: ${error.message}`, error);
  }
  
  // Wrap truly unknown errors with original error preserved
  throw new NetworkError(`Unexpected error occurred: ${error.message || error}`, error);
}

// ============================================================================
// PUBLIC API FUNCTIONS
// ============================================================================

/**
 * Performs a GET request with enhanced error handling, timeout, and retry logic
 * 
 * This is the primary function for making GET requests in the Astrantis application.
 * It automatically handles JSON parsing, error classification, and authentication
 * error callbacks. The function maintains backward compatibility while providing
 * advanced features through the options parameter.
 * 
 * @param {string} url - The URL to fetch (relative or absolute)
 * @param {Object} [options={}] - Optional configuration object
 * @param {number} [options.timeout=30000] - Request timeout in milliseconds
 * @param {number} [options.retries=3] - Maximum retry attempts for failed requests
 * @param {Object} [options.headers={}] - Additional headers to include in request
 * @param {AbortSignal} [options.signal] - AbortSignal for request cancellation
 * @param {Function} [options.onRetryProgress] - Callback function called on each retry attempt
 * @param {number} options.onRetryProgress.attempt - Current retry attempt number (1-based)
 * @param {number} options.onRetryProgress.maxRetries - Maximum number of retry attempts
 * @param {number} options.onRetryProgress.delay - Delay in milliseconds before next retry
 * @param {APIError} options.onRetryProgress.error - The error that triggered the retry
 * @param {boolean} options.onRetryProgress.isRetrying - Always true when callback is called
 * @returns {Promise<any>} Parsed JSON response data
 * @throws {AuthenticationError} On 401/403 responses (triggers global handler)
 * @throws {ServerError} On 5xx server errors
 * @throws {ClientError} On 4xx client errors (except auth) or invalid JSON
 * @throws {TimeoutError} When request exceeds timeout limit
 * @throws {NetworkError} On network/connection failures
 * 
 * @example
 * // Basic usage (backward compatible)
 * const users = await getJson('/api/users');
 * 
 * @example
 * // Advanced usage with custom options
 * const data = await getJson('/api/slow-endpoint', {
 *   timeout: 60000,  // 60 second timeout
 *   retries: 5,      // 5 retry attempts
 *   headers: {
 *     'X-Custom-Header': 'value',
 *     'Authorization': 'Bearer token'
 *   }
 * });
 * 
 * @example
 * // Error handling
 * try {
 *   const data = await getJson('/api/protected');
 * } catch (error) {
 *   if (error instanceof AuthenticationError) {
 *     // Handle auth errors (global handler also called)
 *     console.log('Authentication failed');
 *   } else if (error.isRetryable()) {
 *     // Could potentially retry manually
 *     console.log('Request failed but could be retried');
 *   }
 * }
 * 
 * @public
 * @since 1.0.0
 * @version 2.0.0 - Added timeout, retry, and typed error handling
 */
export async function getJson(url, options = {}) {
  try {
    const res = await fetchWithRetry(url, {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Accept': 'application/json',
        ...options.headers
      },
      signal: options.signal
    }, options.retries, options.timeout);

    const text = await res.text();
    let data = null;
    
    // Parse JSON response
    try {
      data = text ? JSON.parse(text) : null;
    } catch (e) {
      throw new ClientError('Invalid JSON response', res.status, text);
    }

    // Handle non-OK responses
    if (!res.ok) {
      const message = (data && (data.message || data.error)) || `HTTP ${res.status}`;
      
      // Authentication errors
      if (res.status === 401 || res.status === 403) {
        const error = new AuthenticationError(message, res.status, data);
        if (authErrorHandler) {
          authErrorHandler(error);
        }
        throw error;
      }
      
      // Server errors (5xx)
      if (res.status >= 500) {
        throw new ServerError(message, res.status, data);
      }
      
      // Client errors (4xx except 401/403)
      throw new ClientError(message, res.status, data);
    }

    return data;
  } catch (error) {
    handleFetchError(error);
  }
}

/**
 * Performs a POST request with enhanced error handling, timeout, and retry logic
 * 
 * This is the primary function for making POST requests in the Astrantis application.
 * It automatically handles JSON serialization/parsing, error classification, and
 * authentication error callbacks. The function maintains backward compatibility
 * while providing advanced features through the options parameter.
 * 
 * @param {string} url - The URL to fetch (relative or absolute)
 * @param {Object} body - Request body data (will be JSON stringified)
 * @param {Object} [options={}] - Optional configuration object
 * @param {number} [options.timeout=30000] - Request timeout in milliseconds
 * @param {number} [options.retries=3] - Maximum retry attempts for failed requests
 * @param {Object} [options.headers={}] - Additional headers to include in request
 * @param {AbortSignal} [options.signal] - AbortSignal for request cancellation
 * @param {Function} [options.onRetryProgress] - Callback function called on each retry attempt
 * @param {number} options.onRetryProgress.attempt - Current retry attempt number (1-based)
 * @param {number} options.onRetryProgress.maxRetries - Maximum number of retry attempts
 * @param {number} options.onRetryProgress.delay - Delay in milliseconds before next retry
 * @param {APIError} options.onRetryProgress.error - The error that triggered the retry
 * @param {boolean} options.onRetryProgress.isRetrying - Always true when callback is called
 * @returns {Promise<any>} Parsed JSON response data
 * @throws {AuthenticationError} On 401/403 responses (triggers global handler)
 * @throws {ServerError} On 5xx server errors
 * @throws {ClientError} On 4xx client errors (except auth) or invalid JSON
 * @throws {TimeoutError} When request exceeds timeout limit
 * @throws {NetworkError} On network/connection failures
 * 
 * @example
 * // Basic usage (backward compatible)
 * const result = await postJson('/api/users', { name: 'John', email: 'john@example.com' });
 * 
 * @example
 * // Advanced usage with custom options
 * const result = await postJson('/api/upload', payload, {
 *   timeout: 120000,  // 2 minute timeout for large uploads
 *   retries: 1,      // Only 1 retry for uploads
 *   headers: {
 *     'X-Upload-Source': 'web-app',
 *     'Content-Type': 'application/json'
 *   }
 * });
 * 
 * @example
 * // Error handling
 * try {
 *   const result = await postJson('/api/login', credentials);
 * } catch (error) {
 *   if (error instanceof AuthenticationError) {
 *     // Handle auth errors (global handler also called)
 *     console.log('Login failed:', error.message);
 *   } else if (error instanceof ClientError) {
 *     // Handle validation errors, etc.
 *     console.log('Request error:', error.status, error.message);
 *   }
 * }
 * 
 * @public
 * @since 1.0.0
 * @version 2.0.0 - Added timeout, retry, and typed error handling
 */
export async function postJson(url, body, options = {}) {
  try {
    const res = await fetchWithRetry(url, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        ...options.headers
      },
      body: JSON.stringify(body || {}),
      signal: options.signal
    }, options.retries, options.timeout);

    const text = await res.text();
    let data = null;
    
    // Parse JSON response
    try {
      data = text ? JSON.parse(text) : null;
    } catch (e) {
      throw new ClientError('Invalid JSON response', res.status, text);
    }

    // Handle non-OK responses
    if (!res.ok) {
      const message = (data && (data.message || data.error)) || `HTTP ${res.status}`;
      
      // Authentication errors
      if (res.status === 401 || res.status === 403) {
        const error = new AuthenticationError(message, res.status, data);
        if (authErrorHandler) {
          authErrorHandler(error);
        }
        throw error;
      }
      
      // Server errors (5xx)
      if (res.status >= 500) {
        throw new ServerError(message, res.status, data);
      }
      
      // Client errors (4xx except 401/403)
      throw new ClientError(message, res.status, data);
    }

    return data;
  } catch (error) {
    handleFetchError(error);
  }
}

/**
 * Performs a PUT request with enhanced error handling, timeout, and retry logic
 * 
 * This function is similar to postJson but uses the PUT HTTP method, which is
 * semantically appropriate for update operations in RESTful APIs.
 * 
 * @param {string} url - The URL to fetch (relative or absolute)
 * @param {Object} body - Request body data (will be JSON stringified)
 * @param {Object} [options={}] - Optional configuration object
 * @param {number} [options.timeout=30000] - Request timeout in milliseconds
 * @param {number} [options.retries=3] - Maximum retry attempts for failed requests
 * @param {Object} [options.headers={}] - Additional headers to include in request
 * @param {AbortSignal} [options.signal] - AbortSignal for request cancellation
 * @param {Function} [options.onRetryProgress] - Callback function called on each retry attempt
 * @param {number} options.onRetryProgress.attempt - Current retry attempt number (1-based)
 * @param {number} options.onRetryProgress.maxRetries - Maximum number of retry attempts
 * @param {number} options.onRetryProgress.delay - Delay in milliseconds before next retry
 * @param {APIError} options.onRetryProgress.error - The error that triggered the retry
 * @param {boolean} options.onRetryProgress.isRetrying - Always true when callback is called
 * @returns {Promise<any>} Parsed JSON response data
 * @throws {AuthenticationError} On 401/403 responses (triggers global handler)
 * @throws {ServerError} On 5xx server errors
 * @throws {ClientError} On 4xx client errors (except auth) or invalid JSON
 * @throws {TimeoutError} When request exceeds timeout limit
 * @throws {NetworkError} On network/connection failures
 * 
 * @example
 * // Basic usage
 * const result = await putJson('/api/post/123', { title: 'Updated Title' });
 * 
 * @example
 * // Advanced usage with custom options
 * const result = await putJson('/api/post/123', payload, {
 *   timeout: 60000,
 *   retries: 2,
 *   headers: { 'X-Custom': 'value' },
 *   signal: abortController.signal
 * });
 * 
 * @public
 * @since 2.1.0
 */
export async function putJson(url, body, options = {}) {
  try {
    const res = await fetchWithRetry(url, {
      method: 'PUT',
      credentials: 'include',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        ...options.headers
      },
      body: JSON.stringify(body || {}),
      signal: options.signal
    }, options.retries, options.timeout);

    const text = await res.text();
    let data = null;
    
    // Parse JSON response
    try {
      data = text ? JSON.parse(text) : null;
    } catch (e) {
      throw new ClientError('Invalid JSON response', res.status, text);
    }

    // Handle non-OK responses
    if (!res.ok) {
      const message = (data && (data.message || data.error)) || `HTTP ${res.status}`;
      
      // Authentication errors
      if (res.status === 401 || res.status === 403) {
        const error = new AuthenticationError(message, res.status, data);
        if (authErrorHandler) {
          authErrorHandler(error);
        }
        throw error;
      }
      
      // Server errors (5xx)
      if (res.status >= 500) {
        throw new ServerError(message, res.status, data);
      }
      
      // Client errors (4xx except 401/403)
      throw new ClientError(message, res.status, data);
    }

    return data;
  } catch (error) {
    handleFetchError(error);
  }
}

/**
 * Performs a DELETE request with enhanced error handling, timeout, and retry logic
 * 
 * This function uses the DELETE HTTP method, which is semantically appropriate
 * for delete operations in RESTful APIs. The body parameter is optional for DELETE
 * requests, as some APIs may use query parameters or path parameters instead.
 * 
 * @param {string} url - The URL to fetch (relative or absolute)
 * @param {Object} [options={}] - Optional configuration object
 * @param {Object} [options.body] - Optional request body data (will be JSON stringified)
 * @param {number} [options.timeout=30000] - Request timeout in milliseconds
 * @param {number} [options.retries=3] - Maximum retry attempts for failed requests
 * @param {Object} [options.headers={}] - Additional headers to include in request
 * @param {AbortSignal} [options.signal] - AbortSignal for request cancellation
 * @param {Function} [options.onRetryProgress] - Callback function called on each retry attempt
 * @param {number} options.onRetryProgress.attempt - Current retry attempt number (1-based)
 * @param {number} options.onRetryProgress.maxRetries - Maximum number of retry attempts
 * @param {number} options.onRetryProgress.delay - Delay in milliseconds before next retry
 * @param {APIError} options.onRetryProgress.error - The error that triggered the retry
 * @param {boolean} options.onRetryProgress.isRetrying - Always true when callback is called
 * @returns {Promise<any>} Parsed JSON response data
 * @throws {AuthenticationError} On 401/403 responses (triggers global handler)
 * @throws {ServerError} On 5xx server errors
 * @throws {ClientError} On 4xx client errors (except auth) or invalid JSON
 * @throws {TimeoutError} When request exceeds timeout limit
 * @throws {NetworkError} On network/connection failures
 * 
 * @example
 * // Basic usage with query parameters
 * const result = await deleteJson('/api/post?id=123');
 * 
 * @example
 * // Usage with body (if API requires it)
 * const result = await deleteJson('/api/post/123', {
 *   body: { reason: 'spam' }
 * });
 * 
 * @example
 * // Advanced usage with custom options
 * const result = await deleteJson('/api/post/123', {
 *   timeout: 60000,
 *   retries: 1,
 *   headers: { 'X-Force': 'true' }
 * });
 * 
 * @public
 * @since 2.1.0
 */
export async function deleteJson(url, options = {}) {
  try {
    const fetchOptions = {
      method: 'DELETE',
      credentials: 'include',
      headers: {
        'Accept': 'application/json',
        ...options.headers
      },
      signal: options.signal
    };

    // Add body if provided
    if (options.body) {
      fetchOptions.headers['Content-Type'] = 'application/json';
      fetchOptions.body = JSON.stringify(options.body);
    }

    const res = await fetchWithRetry(url, fetchOptions, options.retries, options.timeout);

    const text = await res.text();
    let data = null;
    
    // Parse JSON response
    try {
      data = text ? JSON.parse(text) : null;
    } catch (e) {
      throw new ClientError('Invalid JSON response', res.status, text);
    }

    // Handle non-OK responses
    if (!res.ok) {
      const message = (data && (data.message || data.error)) || `HTTP ${res.status}`;
      
      // Authentication errors
      if (res.status === 401 || res.status === 403) {
        const error = new AuthenticationError(message, res.status, data);
        if (authErrorHandler) {
          authErrorHandler(error);
        }
        throw error;
      }
      
      // Server errors (5xx)
      if (res.status >= 500) {
        throw new ServerError(message, res.status, data);
      }
      
      // Client errors (4xx except 401/403)
      throw new ClientError(message, res.status, data);
    }

    return data;
  } catch (error) {
    handleFetchError(error);
  }
}

